import { Box, Chip, Grid, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getItems } from "../../../api/items";
import { useUser } from "../../../api/useAuth";
import { monthLabelFromIndex, quarterIndexesInRange } from "../../../dashboard-framework/aggregateRows";
import { buildWidgetFilter } from "../../../dashboard-framework/buildWidgetFilter";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { useTechnicianColors, applyTechnicianSeriesColors } from "../../../dashboard-framework/technicianColors";
import { useDashboardAccess } from "../../../dashboard-framework/DashboardAccessProvider";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { formatDateRangePt, TREATMENT_STATUS_LABELS_PT } from "../../../dashboard-framework/describeWidgetLogic";
import { formatDecimal, formatPercent, formatQuantity } from "../../../dashboard-framework/format";
import { buildPivot, PivotMeasure, PivotNode } from "../../../dashboard-framework/pivot";
import { DrillDownColumn, DrillDownConfig, WidgetExplanationContent } from "../../../dashboard-framework/types";
import { useGlobalFilterValues } from "../../../dashboard-framework/useGlobalFilterValues";
import { DataTableCard } from "../../../dashboard-framework/widgets/DataTableCard";
import { customerSurveyRoute, formatDrillDate, formatDrillDateTime, treatmentRoute } from "../shared/drillColumns";
import { fetchAllItemPages } from "../shared/fetchAllItemPages";
import { PivotTableCard } from "../../../dashboard-framework/widgets/PivotTableCard";
import { StackedBarChart } from "../../../dashboard-framework/widgets/StackedBarChart";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { TimeSeriesChart, TimeSeriesTooltipConfig } from "../../../dashboard-framework/widgets/TimeSeriesChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { whiteCardSx } from "../../../dashboard-framework/widgets/widgetStyles";
import { NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import {
    ACTIVE_TREATMENTS_FIELDS,
    activeTreatmentsBaseFilter,
    ATTENDANCE_TARGET_PCT,
    CASE_MANAGER_SESSIONS_FIELDS,
    caseManagerSessionsBaseFilter,
    caseManagerViewerFilter,
    EXCLUDED_PERSON_NAMES,
    FINISHED_TREATMENTS_FIELDS,
    finishedTreatmentsBaseFilter,
    PSY_FINISHED_TREATMENTS_FIELDS,
    PSY_SPANS_FIELDS,
    PSY_SPANS_TREATMENT_CHUNK_SIZE,
    PSYCHOLOGIST_SESSIONS_FIELDS,
    psychologistSessionsBaseFilter,
    psychologistViewerFilter,
    psyFinishedTreatmentsBaseFilter,
    psyTherapistTreatmentsFilter,
    SATISFACTION_SURVEYS_FIELDS,
    satisfactionBaseFilter,
    satisfactionOwnerViewerFilter,
    satisfactionPsychologistViewerFilter,
    TEAM_KPIS_SURVEY_WARNING,
    teamKpisGlobalFilters,
    treatmentsOwnerFilter,
    treatmentsWindowFilter,
} from "./teamKpisConfig";
import {
    activeOutcomeTreatmentsOf,
    activePsyCreditsOf,
    avgDoneSessionsOf,
    clientTypeTreatmentsOf,
    COARSE_DISORDER_GROUPS,
    CoarseDisorderGroupKey,
    ClientTypeKey,
    coarseDisorderGroupLabel,
    coarseDisorderGroupOf,
    distinctTreatmentsOfCredits,
    finishedOutcomeTreatmentsOf,
    finishedPsyCreditsOf,
    outcomeDrillRowOf,
    OutcomeDrillRow,
    OutcomeFilters,
    outcomeOwnerNameOf,
    OutcomeTreatmentBase,
    OutcomeTreatmentItem,
    PsyOutcomeCredit,
    PsyOutcomeTreatmentItem,
    scopedActiveOwnerTreatments,
    scopedActivePsyCredits,
    scopedOutcomeTreatments,
    scopedPsyOutcomeCredits,
    successPctOf,
} from "./teamKpisOutcomes";
import {
    ActivesDiscipline,
    ActivesDisciplineSegment,
    ActivesDrillRow,
    activesPhaseDrillRowsOf,
    ActivesPhaseSummary,
    ActivesSummary,
    activesSummaryOf,
    ActiveTreatmentItem,
    activeTreatmentIdsForSpansOf,
    nfbActivesOfPhase,
    TotalActivesDrillRow,
    totalActivesDrillRowsOf,
    totalActivesSummaryOf,
    TotalActivesSummary,
} from "./teamKpisActives";
import { ServiceProvidedSpan, SpansByTreatment } from "./teamKpisSpans";
import { useDrillDownDialog } from "../../../dashboard-framework/DrillDownProvider";
import {
    AttendanceItem,
    attendanceItemsFromOwnerSessionItems,
    attendanceItemsFromPsychologistSessionItems,
    attendanceRateOfItems,
    monthlyAttendanceTableRows,
    ownerTreatmentRowsFromSessions,
    personAttendanceBars,
    personSurveyDrillRows,
    psychologistTreatmentRowsFromSessions,
    SurveyDrillRow,
    TreatmentDrillRow,
    SatisfactionRow,
    satisfactionQuarterChart,
    satisfactionTableRows,
    surveyPersonItemsFromSurveys,
    surveyQuarterItemsFromSurveys,
    TREND_COUNT_SUFFIX,
} from "./teamKpisRows";

const FIVE_MINUTES = 5 * 60 * 1000;

const fetchAllPages = async (entity: string, fieldsQuery: string, filter: any) => {
    const { items } = await fetchAllItemPages(entity, fieldsQuery, filter);
    return items;
};

const ATTENDANCE_MEASURES = [
    {
        key: "sessions",
        label: "Sessões",
        isNumeric: true,
        compute: (cellItems: Array<AttendanceItem>) => cellItems.reduce((sum, item) => sum + item.count, 0),
        format: (value: number | null) => (value === null ? "—" : formatQuantity(value)),
    },
    {
        key: "rate",
        label: "% comparência",
        isNumeric: true,
        compute: (cellItems: Array<AttendanceItem>) => attendanceRateOfItems(cellItems),
        format: (value: number | null) => (value === null ? "—" : formatPercent(value, 1)),
    },
];

const byMonthIndexAscending = (firstNode: PivotNode<AttendanceItem>, secondNode: PivotNode<AttendanceItem>) => {
    const comparison = firstNode.items[0].monthIndex - secondNode.items[0].monthIndex;
    return comparison;
};

const attendancePivotOf = (items: Array<AttendanceItem>) => {
    const pivot = buildPivot({
        items: items.filter((item) => !EXCLUDED_PERSON_NAMES.includes(item.personName)),
        rowLevels: [(item) => monthLabelFromIndex(item.monthIndex), (item) => item.personName],
        measures: ATTENDANCE_MEASURES,
        sortRowsBy: [byMonthIndexAscending],
    });
    return pivot;
};

// Colunas das modais de drill por tratamento (modo "rows": as linhas já estão em memória, derivadas
// do MESMO fetch/predicado do widget). 1 linha por (tratamento, pessoa) com breakdown das sessões
// contadas; clicar abre a ficha do tratamento. Só muda o rótulo da pessoa entre secções.
const treatmentsDrillColumnsOf = (personLabel: string): Array<DrillDownColumn> => [
    { key: "clientNumber", label: "Nº cliente" },
    { key: "personName", label: personLabel },
    {
        key: "statusNew",
        label: "Estado",
        valueFor: (row: TreatmentDrillRow) => (row.statusNew ? TREATMENT_STATUS_LABELS_PT[row.statusNew] || row.statusNew : null),
    },
    { key: "dateOfStart", label: "Início", valueFor: (row: TreatmentDrillRow) => formatDrillDate(row.dateOfStart) },
    { key: "sessions", label: "Sessões", isNumeric: true },
    { key: "done", label: "Realizadas", isNumeric: true },
    { key: "missed", label: "Faltas", isNumeric: true },
    { key: "cancelled", label: "Canceladas", isNumeric: true },
    {
        key: "ratePct",
        label: "% comparência",
        isNumeric: true,
        valueFor: (row: TreatmentDrillRow) => (row.ratePct === null ? null : formatPercent(row.ratePct, 1)),
    },
];

const caseManagerTreatmentsDrillColumns = treatmentsDrillColumnsOf("Gestora");
const psychologistTreatmentsDrillColumns = treatmentsDrillColumnsOf("Psicóloga");

// ---- Tratamentos por gestora de caso e por psicóloga (pivots + drill) ----

const outcomeDrillColumnsOf = (personLabel: string, sessionsLabel: string): Array<DrillDownColumn> => [
    { key: "clientNumber", label: "Nº cliente" },
    { key: "personName", label: personLabel },
    { key: "clientTypeLabel", label: "Tipo" },
    { key: "disorderLabel", label: "Perturbação" },
    {
        key: "statusNew",
        label: "Estado",
        valueFor: (row: OutcomeDrillRow) => (row.statusNew ? TREATMENT_STATUS_LABELS_PT[row.statusNew] || row.statusNew : null),
    },
    { key: "dateOfStart", label: "Início", valueFor: (row: OutcomeDrillRow) => formatDrillDate(row.dateOfStart) },
    { key: "dateIfFinish", label: "Fim", valueFor: (row: OutcomeDrillRow) => formatDrillDate(row.dateIfFinish) },
    { key: "doneSessions", label: sessionsLabel, isNumeric: true },
];

const caseManagerOutcomeDrillColumns = outcomeDrillColumnsOf("Gestora", "Sessões NFB realizadas");
const psychologistOutcomeDrillColumns = outcomeDrillColumnsOf("Psicóloga", "Sessões Psicologia realizadas");

// Medidas dos pivots Pessoa → Perturbação. Os itens misturam tratamentos TERMINADOS (seguem o filtro
// "Período") e ATIVOS (fotografia atual — coluna "Ativos"); as medidas separam por estado. Com o
// seletor "Tipo de cliente" num tipo específico, as medidas de split ficam redundantes e saem.
// treatmentsOfCell converte os itens do nó em tratamentos DISTINTOS (nas psicólogas o item é um
// crédito (tratamento, pessoa) — a célula Total não pode somar o mesmo tratamento duas vezes).
const formatOutcomeCount = (value: number | null) => (value === null ? "—" : formatQuantity(value));
const formatOutcomePct = (value: number | null) => (value === null ? "—" : formatPercent(value, 1));

const ACTIVES_MEASURE_KEY = "actives";
const ACTIVES_PHASE_MEASURE_KEYS: Record<1 | 2, string> = { 1: "activesPhase1", 2: "activesPhase2" };

// Âmbito da coluna "Ativos": a disciplina da tabela decide qual o período de services_provided que
// tem de estar "A Decorrer" (NFB nas gestoras, Psicologia nas psicólogas). includePhaseSplit liga as
// colunas "Ativos F1/F2" — só na tabela das gestoras, porque a fase do NFB é a única com leitura de
// carga (a fase da Psicologia mede cadência, não esforço).
type OutcomeMeasuresOptions<ItemType> = {
    clientType: ClientTypeKey | null;
    disciplineService: number;
    treatmentsOfCell: (cellItems: Array<ItemType>) => Array<OutcomeTreatmentBase>;
    spansByTreatment: SpansByTreatment;
    includePhaseSplit: boolean;
};

const outcomeMeasuresOf = <ItemType,>(options: OutcomeMeasuresOptions<ItemType>): Array<PivotMeasure<ItemType>> => {
    const { clientType, disciplineService, treatmentsOfCell, spansByTreatment, includePhaseSplit } = options;
    const finishedOfCell = (cellItems: Array<ItemType>) => finishedOutcomeTreatmentsOf(treatmentsOfCell(cellItems));
    const activesOfCell = (cellItems: Array<ItemType>) =>
        activeOutcomeTreatmentsOf(treatmentsOfCell(cellItems), spansByTreatment, disciplineService);
    const phaseMeasures: Array<PivotMeasure<ItemType>> = includePhaseSplit
        ? ([1, 2] as Array<1 | 2>).map((phase) => ({
              key: ACTIVES_PHASE_MEASURE_KEYS[phase],
              label: `Ativos F${phase}`,
              isNumeric: true,
              compute: (cellItems: Array<ItemType>) => nfbActivesOfPhase(activesOfCell(cellItems), phase).length,
              format: formatOutcomeCount,
          }))
        : [];
    const baseMeasures: Array<PivotMeasure<ItemType>> = [
        {
            key: ACTIVES_MEASURE_KEY,
            label: "Ativos",
            isNumeric: true,
            compute: (cellItems) => activesOfCell(cellItems).length,
            format: formatOutcomeCount,
        },
        ...phaseMeasures,
        { key: "treatments", label: "Terminados", isNumeric: true, compute: (cellItems) => finishedOfCell(cellItems).length, format: formatOutcomeCount },
        { key: "successPct", label: "% sucesso", isNumeric: true, compute: (cellItems) => successPctOf(finishedOfCell(cellItems)), format: formatOutcomePct },
    ];
    const splitMeasures: Array<PivotMeasure<ItemType>> = [
        {
            key: "adults",
            label: "Adultos",
            isNumeric: true,
            compute: (cellItems) => clientTypeTreatmentsOf(finishedOfCell(cellItems), "adults").length,
            format: formatOutcomeCount,
        },
        {
            key: "adultsSuccessPct",
            label: "% suc. adultos",
            isNumeric: true,
            compute: (cellItems) => successPctOf(clientTypeTreatmentsOf(finishedOfCell(cellItems), "adults")),
            format: formatOutcomePct,
        },
        {
            key: "children",
            label: "Crianças",
            isNumeric: true,
            compute: (cellItems) => clientTypeTreatmentsOf(finishedOfCell(cellItems), "children").length,
            format: formatOutcomeCount,
        },
        {
            key: "childrenSuccessPct",
            label: "% suc. crianças",
            isNumeric: true,
            compute: (cellItems) => successPctOf(clientTypeTreatmentsOf(finishedOfCell(cellItems), "children")),
            format: formatOutcomePct,
        },
    ];
    const averageMeasures: Array<PivotMeasure<ItemType>> = [
        {
            key: "avgDoneSessions",
            label: "Média sessões",
            isNumeric: true,
            compute: (cellItems) => avgDoneSessionsOf(finishedOfCell(cellItems), disciplineService),
            format: (value) => (value === null ? "—" : formatDecimal(value, 1)),
        },
    ];
    const measures = clientType === null ? [...baseMeasures, ...splitMeasures, ...averageMeasures] : [...baseMeasures, ...averageMeasures];
    return measures;
};

// Grupos de perturbação (2º nível) ficam alfabéticos (defeito) nas duas tabelas.
// Pessoas por nº de ATIVOS descendente — as duas tabelas servem para ver carga atual, e quem tem
// mais tratamentos a decorrer tem de estar no topo.
const byActivesDescendingOf = <ItemType,>(
    treatmentsOfCell: (cellItems: Array<ItemType>) => Array<OutcomeTreatmentBase>,
    spansByTreatment: SpansByTreatment,
    disciplineService: number
) => {
    const activesCountOf = (node: PivotNode<ItemType>) =>
        activeOutcomeTreatmentsOf(treatmentsOfCell(node.items), spansByTreatment, disciplineService).length;
    const comparator = (firstNode: PivotNode<ItemType>, secondNode: PivotNode<ItemType>) => {
        const comparison = activesCountOf(secondNode) - activesCountOf(firstNode) || firstNode.label.localeCompare(secondNode.label);
        return comparison;
    };
    return comparator;
};

const itemsOfCell = (cellItems: Array<OutcomeTreatmentItem>) => cellItems;

const outcomePivotOf = (treatments: Array<OutcomeTreatmentItem>, clientType: ClientTypeKey | null, spansByTreatment: SpansByTreatment) => {
    const pivot = buildPivot({
        items: treatments,
        rowLevels: [outcomeOwnerNameOf, (treatment) => coarseDisorderGroupLabel(coarseDisorderGroupOf(treatment.diagnose_main?.id))],
        measures: outcomeMeasuresOf<OutcomeTreatmentItem>({
            clientType,
            disciplineService: NFB_SERVICE,
            treatmentsOfCell: itemsOfCell,
            spansByTreatment,
            includePhaseSplit: true,
        }),
        sortRowsBy: [byActivesDescendingOf<OutcomeTreatmentItem>(itemsOfCell, spansByTreatment, NFB_SERVICE)],
    });
    return pivot;
};

const psyOutcomePivotOf = (credits: Array<PsyOutcomeCredit>, clientType: ClientTypeKey | null, spansByTreatment: SpansByTreatment) => {
    const pivot = buildPivot({
        items: credits,
        rowLevels: [(credit) => credit.personName, (credit) => coarseDisorderGroupLabel(coarseDisorderGroupOf(credit.treatment.diagnose_main?.id))],
        measures: outcomeMeasuresOf<PsyOutcomeCredit>({
            clientType,
            disciplineService: PSY_SERVICE,
            treatmentsOfCell: distinctTreatmentsOfCredits,
            spansByTreatment,
            includePhaseSplit: false,
        }),
        sortRowsBy: [byActivesDescendingOf<PsyOutcomeCredit>(distinctTreatmentsOfCredits, spansByTreatment, PSY_SERVICE)],
    });
    return pivot;
};

// ---- Cartões "Clientes ativos" (portados do extinto Treatment Performance Dashboard) ----

// services_provided por tratamento: chunks pelo escalar `treatment` (raiz, fiável em _in). Alimenta a
// fase da Psicologia nos cartões E a condição "A Decorrer" da coluna "Ativos" das duas tabelas.
const fetchServiceSpans = async (treatmentIds: Array<number>) => {
    const spansByTreatment: SpansByTreatment = {};
    for (let start = 0; start < treatmentIds.length; start += PSY_SPANS_TREATMENT_CHUNK_SIZE) {
        const chunk = treatmentIds.slice(start, start + PSY_SPANS_TREATMENT_CHUNK_SIZE);
        const { items } = await getItems("treatment_prescription", PSY_SPANS_FIELDS, { filter: { treatment: { _in: chunk } }, pageSize: 1000, sort: "id" });
        items.forEach((prescription: any) => {
            const treatmentId = Number(prescription.treatment);
            if (!treatmentId) return;
            const spans = (prescription.services_provided || []) as Array<ServiceProvidedSpan>;
            spansByTreatment[treatmentId] = (spansByTreatment[treatmentId] || []).concat(spans);
        });
    }
    return spansByTreatment;
};

const activesGroupChips = (byGroup: ActivesPhaseSummary["byGroup"]) => {
    const groupsWithCount = COARSE_DISORDER_GROUPS.filter((group) => (byGroup[group.key] || 0) > 0);
    if (groupsWithCount.length === 0) {
        const placeholder = (
            <Typography variant="caption" color="text.secondary">
                —
            </Typography>
        );
        return placeholder;
    }
    const chips = (
        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.75}>
            {groupsWithCount.map((group) => (
                <Chip key={group.key} size="small" variant="outlined" label={`${group.label} ${byGroup[group.key]}`} />
            ))}
        </Box>
    );
    return chips;
};

// Colunas do drill dos cartões de fase (modo "rows": tratamentos ativos da disciplina naquela fase).
const activesDrillColumns: Array<DrillDownColumn> = [
    { key: "clientNumber", label: "Nº cliente" },
    { key: "clientTypeLabel", label: "Tipo" },
    { key: "disorderLabel", label: "Perturbação" },
    { key: "dateOfStart", label: "Início", valueFor: (row: ActivesDrillRow) => formatDrillDate(row.dateOfStart) },
    { key: "doneSessions", label: "Sessões realizadas", isNumeric: true },
];

type ActivesPhaseBoxProps = { label: string; phaseSummary: ActivesPhaseSummary; drillDown: DrillDownConfig | null };

const ActivesPhaseBox = ({ label, phaseSummary, drillDown }: ActivesPhaseBoxProps) => {
    const drillDownDialog = useDrillDownDialog();
    const isClickable = Boolean(drillDown && drillDownDialog);

    const openDrillDown = () => {
        if (drillDown && drillDownDialog) drillDownDialog.openDrillDown(drillDown);
    };

    return (
        <Paper
            elevation={0}
            onClick={isClickable ? openDrillDown : undefined}
            sx={{ ...whiteCardSx, height: "100%", ...(isClickable ? { cursor: "pointer", "&:hover": { backgroundColor: "#f1f3f6" } } : {}) }}
        >
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
                {formatQuantity(phaseSummary.count)}
            </Typography>
            {activesGroupChips(phaseSummary.byGroup)}
        </Paper>
    );
};

const activesCard = (
    title: string,
    summary: ActivesSummary,
    explanation: WidgetExplanationContent,
    isLoading: boolean,
    isError: boolean,
    phaseDrillDownOf: (phase: 1 | 2) => DrillDownConfig | null
) => {
    const card = (
        <WidgetCard title={title} explanation={explanation} isLoading={isLoading} isError={isError}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <StatCard label="Ativos (total)" value={formatQuantity(summary.total)} color={DASHBOARD_COLORS.highlight} explanation={explanation} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ActivesPhaseBox label="Fase 1" phaseSummary={summary.phase1} drillDown={phaseDrillDownOf(1)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ActivesPhaseBox label="Fase 2" phaseSummary={summary.phase2} drillDown={phaseDrillDownOf(2)} />
                </Grid>
            </Grid>
        </WidgetCard>
    );
    return card;
};

// Colunas do drill das caixas do total: a lista mistura disciplinas, por isso as sessões realizadas
// aparecem em duas colunas.
const totalActivesDrillColumns: Array<DrillDownColumn> = [
    { key: "clientNumber", label: "Nº cliente" },
    { key: "clientTypeLabel", label: "Tipo" },
    { key: "disorderLabel", label: "Perturbação" },
    { key: "dateOfStart", label: "Início", valueFor: (row: TotalActivesDrillRow) => formatDrillDate(row.dateOfStart) },
    { key: "doneNfbSessions", label: "Sessões NFB", isNumeric: true },
    { key: "donePsySessions", label: "Sessões Psicologia", isNumeric: true },
];

const TOTAL_ACTIVES_SEGMENT_LABELS: Record<ActivesDisciplineSegment, string> = {
    onlyNfb: "Só NFB",
    onlyPsy: "Só Psicologia",
    both: "Ambos",
};

// "Ambos" primeiro (logo depois do total): é a caixa que explica porque os dois cartões abaixo não se somam.
const TOTAL_ACTIVES_SEGMENTS: Array<ActivesDisciplineSegment> = ["both", "onlyNfb", "onlyPsy"];

const totalActivesCard = (
    summary: TotalActivesSummary,
    explanation: WidgetExplanationContent,
    isLoading: boolean,
    isError: boolean,
    segmentDrillDownOf: (segment: ActivesDisciplineSegment) => DrillDownConfig | null
) => {
    const card = (
        <WidgetCard title="Clientes ativos — Total" explanation={explanation} isLoading={isLoading} isError={isError}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard label="Ativos (total)" value={formatQuantity(summary.total)} color={DASHBOARD_COLORS.highlight} explanation={explanation} />
                </Grid>
                {TOTAL_ACTIVES_SEGMENTS.map((segment) => (
                    <Grid item xs={12} sm={6} md={3} key={segment}>
                        <ActivesPhaseBox
                            label={TOTAL_ACTIVES_SEGMENT_LABELS[segment]}
                            phaseSummary={summary[segment]}
                            drillDown={segmentDrillDownOf(segment)}
                        />
                    </Grid>
                ))}
            </Grid>
        </WidgetCard>
    );
    return card;
};

const HEADER_SELECT_ALL_VALUE = "__all";

type HeaderSelectProps = {
    label: string;
    allLabel: string;
    options: Array<{ value: string; label: string }>;
    value: string | null;
    onChange: (value: string | null) => void;
};

const HeaderSelect = ({ label, allLabel, options, value, onChange }: HeaderSelectProps) => {
    return (
        <TextField
            select
            size="small"
            label={label}
            value={value === null ? HEADER_SELECT_ALL_VALUE : value}
            onChange={(event) => {
                const newValue = event.target.value;
                onChange(newValue === HEADER_SELECT_ALL_VALUE ? null : newValue);
            }}
            sx={{ minWidth: 160 }}
        >
            <MenuItem value={HEADER_SELECT_ALL_VALUE}>{allLabel}</MenuItem>
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
};

const OUTCOME_CLIENT_TYPE_OPTIONS = [
    { value: "adults", label: "Adultos" },
    { value: "children", label: "Crianças" },
];

const attendanceBarsCard = (
    title: string,
    explanation: WidgetExplanationContent,
    items: Array<AttendanceItem>,
    isLoading: boolean,
    isError: boolean,
    barDrillDown?: (personKey: string, personName: string) => DrillDownConfig | null
) => {
    const bars = personAttendanceBars(items, EXCLUDED_PERSON_NAMES);
    const points = bars.map((bar) => ({ person: bar.personName, personKey: bar.personKey, rate: Math.round((bar.ratePct ?? 0) * 100) / 100 }));
    const card = (
        <WidgetCard
            title={title}
            explanation={explanation}
            isLoading={isLoading}
            isError={isError}
        >
            <StackedBarChart
                points={points}
                series={[{ key: "rate", label: "% comparência", color: DASHBOARD_COLORS.highlight }]}
                categoryKey="person"
                valueFormatter={(value) => formatPercent(value, 2)}
                referenceLine={{ value: ATTENDANCE_TARGET_PCT, label: `Objetivo ${ATTENDANCE_TARGET_PCT}%` }}
                showBarLabels
                barDrillDown={barDrillDown ? (point) => barDrillDown(String(point.personKey), String(point.person)) : undefined}
            />
        </WidgetCard>
    );
    return card;
};

const SATISFACTION_COLUMNS = [
    { key: "person", label: "Pessoa" },
    { key: "avg", label: "Média (0–10)", isNumeric: true },
    { key: "surveys", label: "Questionários", isNumeric: true },
];

const SURVEY_MOMENT_LABELS_PT: Record<string, string> = { intermediate: "Intermédio", end: "Final" };

// Drill das tabelas de satisfação: os questionários distintos da pessoa clicada, com nota,
// momento e comentário; clicar numa linha abre a ficha do questionário.
const satisfactionDrillColumns: Array<DrillDownColumn> = [
    { key: "dateSent", label: "Enviado", valueFor: (row: SurveyDrillRow) => formatDrillDateTime(row.dateSent) },
    { key: "wouldRecommend", label: "Nota (0–10)", isNumeric: true },
    {
        key: "moment",
        label: "Momento",
        valueFor: (row: SurveyDrillRow) => (row.moment ? SURVEY_MOMENT_LABELS_PT[row.moment] || row.moment : null),
    },
    { key: "comments", label: "Comentários" },
];

const satisfactionDisplayRows = (rows: Array<SatisfactionRow>) => {
    const displayRows = rows.map((row) => ({
        person: row.personName,
        avg: row.avg === null ? "—" : formatDecimal(row.avg, 2),
        surveys: formatQuantity(row.surveys),
    }));
    return displayRows;
};

export const TeamKpisDashboard = () => {
    const access = useDashboardAccess();
    const userResult = useUser();
    const currentUserId: string | null = userResult.data?.id || null;
    const { values, setValue } = useGlobalFilterValues(teamKpisGlobalFilters);

    const now = new Date();
    const timelineRange = resolveRequiredDateFilterRange(values, "timeline", "este_ano", now);
    const timelineFilter = buildWidgetFilter({
        respondsTo: ["timeline"],
        globalFilters: teamKpisGlobalFilters,
        globalValues: values,
        now,
    });

    // Filtro "Utilizadores" (client-side): por defeito só ativos; "Ativos e suspensos" inclui quem já saiu.
    const includeSuspended = values.staffScope === "all";
    const scopeStaff = (items: Array<AttendanceItem>) => (includeSuspended ? items : items.filter((item) => item.personActive));

    const viewerParams = access.viewerParams || {};
    const resolveParamUserId = (paramValue: string | number | undefined) => {
        if (paramValue === undefined || paramValue === null) return null;
        const resolved = paramValue === "$current_user" ? currentUserId : String(paramValue);
        return resolved;
    };
    const psychologistUserId = resolveParamUserId(viewerParams.psychologistUserId);
    const caseManagerUserId = resolveParamUserId(viewerParams.caseManagerUserId);
    const isDirectorView = psychologistUserId === null && caseManagerUserId === null;

    const showPsychologistsSection = isDirectorView || psychologistUserId !== null;
    const showCaseManagersSection = isDirectorView || caseManagerUserId !== null;

    // Ambas as secções (e AMBAS as vistas): fetch raw paginado + rollup client-side — a regra do
    // compromisso e a exclusão owner-é-psicóloga não são exprimíveis em aggregates do Directus.
    // Na vista própria o mesmo fetch leva o viewer filter, por isso os números batem com a diretora.
    const psychologistSessionsFilter = {
        _and: [psychologistSessionsBaseFilter, timelineFilter, ...(psychologistUserId ? [psychologistViewerFilter(psychologistUserId)] : [])],
    };
    const psychologistSessionsResult = useQuery({
        queryKey: ["team-kpis-psychologist-sessions", PSYCHOLOGIST_SESSIONS_FIELDS, timelineRange.gte, timelineRange.lt, psychologistUserId],
        queryFn: () => fetchAllPages("sessions", PSYCHOLOGIST_SESSIONS_FIELDS, psychologistSessionsFilter),
        enabled: showPsychologistsSection,
        staleTime: FIVE_MINUTES,
    });
    const psychologistItems = scopeStaff(attendanceItemsFromPsychologistSessionItems(psychologistSessionsResult.data || []));
    const psychologistsLoading = psychologistSessionsResult.isLoading;
    const psychologistsError = psychologistSessionsResult.isError;

    const ownerSessionsFilter = {
        _and: [caseManagerSessionsBaseFilter, timelineFilter, ...(caseManagerUserId ? [caseManagerViewerFilter(caseManagerUserId)] : [])],
    };
    const ownerSessionsResult = useQuery({
        queryKey: ["team-kpis-owner-sessions", CASE_MANAGER_SESSIONS_FIELDS, timelineRange.gte, timelineRange.lt, caseManagerUserId],
        queryFn: () => fetchAllPages("sessions", CASE_MANAGER_SESSIONS_FIELDS, ownerSessionsFilter),
        enabled: showCaseManagersSection,
        staleTime: FIVE_MINUTES,
    });
    const caseManagerItems = scopeStaff(attendanceItemsFromOwnerSessionItems(ownerSessionsResult.data || []));
    const caseManagersLoading = ownerSessionsResult.isLoading;
    const caseManagersError = ownerSessionsResult.isError;

    // Satisfação: 1 fetch com os dois caminhos de atribuição; dedup/médias client-side.
    const dateSentRange = timelineRange;
    const surveysViewerClause = psychologistUserId
        ? [satisfactionPsychologistViewerFilter(psychologistUserId)]
        : caseManagerUserId
          ? [satisfactionOwnerViewerFilter(caseManagerUserId)]
          : [];
    const surveysFilter = { _and: [satisfactionBaseFilter(dateSentRange), ...surveysViewerClause] };
    const surveysResult = useQuery({
        queryKey: ["team-kpis-surveys", SATISFACTION_SURVEYS_FIELDS, dateSentRange.gte, dateSentRange.lt, psychologistUserId, caseManagerUserId],
        queryFn: () => fetchAllPages("customer_surveys", SATISFACTION_SURVEYS_FIELDS, surveysFilter),
        enabled: isDirectorView || psychologistUserId !== null || caseManagerUserId !== null,
        staleTime: FIVE_MINUTES,
    });
    const surveyItems = surveysResult.data || [];

    const psychologistSatisfaction = satisfactionTableRows(
        surveyPersonItemsFromSurveys(surveyItems, "psychologists").filter(
            (item) => psychologistUserId === null || item.personKey === psychologistUserId
        ),
        includeSuspended
    );
    const ownerSatisfaction = satisfactionTableRows(
        surveyPersonItemsFromSurveys(surveyItems, "owner").filter(
            (item) => caseManagerUserId === null || item.personKey === caseManagerUserId
        ),
        includeSuspended
    );

    // Drill das tabelas de satisfação (modo "rows": mesma extração/exclusões das tabelas).
    const satisfactionRowDrillDown = (rows: Array<SatisfactionRow>, attribution: "psychologists" | "owner") => (rowIndex: number) => {
        const personRow = rows[rowIndex];
        if (!personRow) return null;
        const config: DrillDownConfig = {
            kind: "rows",
            title: `Questionários de satisfação — ${personRow.personName}`,
            columns: satisfactionDrillColumns,
            rows: personSurveyDrillRows(surveyItems, attribution, personRow.personKey),
            defaultSortKey: "-dateSent",
            rowRoute: customerSurveyRoute,
            queryDescription:
                attribution === "owner"
                    ? "Questionários distintos atribuídos à gestora (excluindo tratamentos em que a gestora é também psicóloga do tratamento) — mesma contagem da tabela."
                    : "Questionários distintos atribuídos à psicóloga (via terapeutas envolvidos no tratamento) — mesma contagem da tabela.",
        };
        return config;
    };

    // ---- Séries temporais por trimestre: obedecem ao filtro global "Período" ----
    const trendQuarterIndexes = quarterIndexesInRange(timelineRange, now);

    // Cada linha por pessoa usa a cor definida na ficha do técnico (technicians.color); sem cor definida cai na paleta de reserva.
    const { technicianColorByUserId } = useTechnicianColors();

    // Satisfação por trimestre: reaproveita os questionários já obtidos para as tabelas (mesmo filtro "Período").
    const psychologistTrend = satisfactionQuarterChart(
        surveyQuarterItemsFromSurveys(surveyItems, "psychologists"),
        trendQuarterIndexes,
        psychologistUserId,
        isDirectorView,
        EXCLUDED_PERSON_NAMES,
        includeSuspended
    );
    const ownerTrend = satisfactionQuarterChart(
        surveyQuarterItemsFromSurveys(surveyItems, "owner"),
        trendQuarterIndexes,
        caseManagerUserId,
        isDirectorView,
        EXCLUDED_PERSON_NAMES,
        includeSuspended
    );

    // ---- Tratamentos por gestora de caso (topo) — tabela com filtros de perturbação e tipo ----
    // client_owner é id de técnica; a vista própria precisa do id de técnica da gestora autenticada.
    const caseManagerTechnicianResult = useQuery({
        queryKey: ["team-kpis-cm-technician", caseManagerUserId],
        queryFn: () => getItems("technicians", "id,user_id.first_name,user_id.last_name", { filter: { user_id: { _eq: caseManagerUserId } }, pageSize: 1, sort: "id" }),
        enabled: caseManagerUserId !== null,
        staleTime: FIVE_MINUTES,
    });
    const ownTechnicianId: number | null = caseManagerTechnicianResult.data?.items?.[0]
        ? Number(caseManagerTechnicianResult.data.items[0].id)
        : null;

    // Fetch raw (AMBAS as vistas): a elegibilidade (≥1 NFB realizada, owner-não-psicóloga) e os filtros
    // de grupo/tipo são client-side; na vista própria o mesmo fetch leva o filtro por client_owner
    // de UM nível (id de técnica, nunca user_id profundo — pode ser ignorado sob token row-scoped).
    const finishedTreatmentsFilter = {
        _and: [
            finishedTreatmentsBaseFilter,
            treatmentsWindowFilter(timelineRange),
            ...(caseManagerUserId !== null && ownTechnicianId !== null ? [treatmentsOwnerFilter(ownTechnicianId)] : []),
        ],
    };
    const finishedTreatmentsResult = useQuery({
        queryKey: ["team-kpis-finished-treatments", FINISHED_TREATMENTS_FIELDS, timelineRange.gte, timelineRange.lt, caseManagerUserId, ownTechnicianId],
        queryFn: () => fetchAllPages("treatments", FINISHED_TREATMENTS_FIELDS, finishedTreatmentsFilter),
        enabled: showCaseManagersSection && (caseManagerUserId === null || ownTechnicianId !== null),
        staleTime: FIVE_MINUTES,
    });

    const [outcomeGroupKey, setOutcomeGroupKey] = useState<CoarseDisorderGroupKey | null>(null);
    const [outcomeClientType, setOutcomeClientType] = useState<ClientTypeKey | null>(null);
    const outcomeFilters: OutcomeFilters = { groupKey: outcomeGroupKey, clientType: outcomeClientType };

    const scopedOutcomeItems = scopedOutcomeTreatments(finishedTreatmentsResult.data || [], outcomeFilters, includeSuspended, EXCLUDED_PERSON_NAMES);

    // Drill por célula do pivot: as linhas derivam dos itens do PRÓPRIO nó (gestora ou gestora+grupo);
    // a coluna clicada decide o subset — "Ativos" abre os ativos, "Ativos F1/F2" a fase respetiva,
    // as restantes abrem os terminados.
    const outcomeCellDrillDown = (node: PivotNode<OutcomeTreatmentItem>, columnKey: string) => {
        const activesPhase = columnKey === ACTIVES_PHASE_MEASURE_KEYS[1] ? 1 : columnKey === ACTIVES_PHASE_MEASURE_KEYS[2] ? 2 : null;
        const isActivesColumn = columnKey === ACTIVES_MEASURE_KEY || activesPhase !== null;
        const activesOfNode = activeOutcomeTreatmentsOf(node.items, spansByTreatment, NFB_SERVICE);
        const treatments = !isActivesColumn
            ? finishedOutcomeTreatmentsOf(node.items)
            : activesPhase === null
              ? activesOfNode
              : nfbActivesOfPhase(activesOfNode, activesPhase);
        if (treatments.length === 0) return null;
        const activesLabel = activesPhase === null ? "ativos" : `ativos (Fase ${activesPhase})`;
        const activesRowLabel = activesPhase === null ? "ativo" : `ativo (Fase ${activesPhase})`;
        const config: DrillDownConfig = {
            kind: "rows",
            title: `Tratamentos ${isActivesColumn ? activesLabel : "terminados"} — ${node.path.join(" — ")}`,
            columns: caseManagerOutcomeDrillColumns,
            rows: treatments.map((treatment) => outcomeDrillRowOf(treatment, outcomeOwnerNameOf(treatment), NFB_SERVICE)),
            defaultSortKey: isActivesColumn ? "-dateOfStart" : "-dateIfFinish",
            rowRoute: treatmentRoute,
            queryDescription:
                `Um tratamento ${isActivesColumn ? activesRowLabel : "terminado"} por linha, com o breakdown das sessões de NFB contadas neste valor ` +
                "(mesmo universo do widget, por isso a contagem reconcilia).",
        };
        return config;
    };

    // ---- Tratamentos por psicóloga — crédito pelas terapeutas do tratamento ----
    const psychologistTechnicianResult = useQuery({
        queryKey: ["team-kpis-psy-technician", psychologistUserId],
        queryFn: () => getItems("technicians", "id,user_id.first_name,user_id.last_name", { filter: { user_id: { _eq: psychologistUserId } }, pageSize: 1, sort: "id" }),
        enabled: psychologistUserId !== null,
        staleTime: FIVE_MINUTES,
    });
    const psyTechnicianId: number | null = psychologistTechnicianResult.data?.items?.[0]
        ? Number(psychologistTechnicianResult.data.items[0].id)
        : null;

    const psyFinishedTreatmentsFilter = {
        _and: [
            psyFinishedTreatmentsBaseFilter,
            treatmentsWindowFilter(timelineRange),
            ...(psychologistUserId !== null && psyTechnicianId !== null ? [psyTherapistTreatmentsFilter(psyTechnicianId)] : []),
        ],
    };
    const psyFinishedTreatmentsResult = useQuery({
        queryKey: ["team-kpis-psy-finished-treatments", PSY_FINISHED_TREATMENTS_FIELDS, timelineRange.gte, timelineRange.lt, psychologistUserId, psyTechnicianId],
        queryFn: () => fetchAllPages("treatments", PSY_FINISHED_TREATMENTS_FIELDS, psyFinishedTreatmentsFilter),
        enabled: showPsychologistsSection && (psychologistUserId === null || psyTechnicianId !== null),
        staleTime: FIVE_MINUTES,
    });

    const [psyOutcomeGroupKey, setPsyOutcomeGroupKey] = useState<CoarseDisorderGroupKey | null>(null);
    const [psyOutcomeClientType, setPsyOutcomeClientType] = useState<ClientTypeKey | null>(null);
    const psyOutcomeFilters: OutcomeFilters = { groupKey: psyOutcomeGroupKey, clientType: psyOutcomeClientType };

    // Vista própria: a fetch já vem filtrada aos tratamentos dela; o restrictToPersonKey tira as
    // colegas do MESMO tratamento das linhas.
    const scopedPsyCredits = scopedPsyOutcomeCredits(
        psyFinishedTreatmentsResult.data || [],
        psyOutcomeFilters,
        includeSuspended,
        EXCLUDED_PERSON_NAMES,
        psychologistUserId
    );

    const psyOutcomeCellDrillDown = (node: PivotNode<PsyOutcomeCredit>, columnKey: string) => {
        const isActivesColumn = columnKey === ACTIVES_MEASURE_KEY;
        const credits = isActivesColumn ? activePsyCreditsOf(node.items, spansByTreatment) : finishedPsyCreditsOf(node.items);
        if (credits.length === 0) return null;
        const config: DrillDownConfig = {
            kind: "rows",
            title: `Tratamentos ${isActivesColumn ? "ativos" : "terminados"} — ${node.path.join(" — ")}`,
            columns: psychologistOutcomeDrillColumns,
            rows: credits.map((credit) => outcomeDrillRowOf(credit.treatment, credit.personName, PSY_SERVICE)),
            defaultSortKey: isActivesColumn ? "-dateOfStart" : "-dateIfFinish",
            rowRoute: treatmentRoute,
            queryDescription:
                `Um tratamento ${isActivesColumn ? "ativo" : "terminado"} por linha, com a perturbação (grupo), o tipo de cliente e as sessões de Psicologia realizadas ` +
                "— mesmo universo e filtros da célula clicada, por isso a contagem reconcilia com o número mostrado.",
        };
        return config;
    };

    // ---- Clientes ativos (topo): snapshot de TODOS os tratamentos "Ativo", sem filtro de data ----
    const activesViewerClause =
        caseManagerUserId !== null && ownTechnicianId !== null
            ? [treatmentsOwnerFilter(ownTechnicianId)]
            : psychologistUserId !== null && psyTechnicianId !== null
              ? [psyTherapistTreatmentsFilter(psyTechnicianId)]
              : [];
    const activesFilter = { _and: [activeTreatmentsBaseFilter, ...activesViewerClause] };
    const activesEnabled =
        isDirectorView ||
        (caseManagerUserId !== null && ownTechnicianId !== null) ||
        (psychologistUserId !== null && psyTechnicianId !== null);
    const activeTreatmentsResult = useQuery({
        queryKey: ["team-kpis-active-treatments", ACTIVE_TREATMENTS_FIELDS, caseManagerUserId, psychologistUserId, ownTechnicianId, psyTechnicianId],
        queryFn: () => fetchAllPages("treatments", ACTIVE_TREATMENTS_FIELDS, activesFilter),
        enabled: activesEnabled,
        staleTime: FIVE_MINUTES,
    });
    const activeTreatmentItems = (activeTreatmentsResult.data || []) as Array<ActiveTreatmentItem>;

    // Spans de TODOS os ativos com disciplina (não só os de Psicologia): a coluna "Ativos" das duas
    // tabelas depende deles, logo a fetch corre também na vista das gestoras.
    const activeSpanTreatmentIds = activeTreatmentIdsForSpansOf(activeTreatmentItems);
    const activeSpansResult = useQuery({
        queryKey: ["team-kpis-active-spans", activeSpanTreatmentIds.join(",")],
        queryFn: () => fetchServiceSpans(activeSpanTreatmentIds),
        enabled: activesEnabled && activeSpanTreatmentIds.length > 0,
        staleTime: FIVE_MINUTES,
    });
    const spansByTreatment = activeSpansResult.data || {};
    const spansLoading = activeSpanTreatmentIds.length > 0 && activeSpansResult.isLoading;

    const totalActivesSummary = totalActivesSummaryOf(activeTreatmentItems);
    const nfbCardActiveItems = scopedActiveOwnerTreatments(
        (activeTreatmentsResult.data || []) as Array<OutcomeTreatmentItem>,
        { groupKey: null, clientType: null },
        includeSuspended,
        EXCLUDED_PERSON_NAMES,
        spansByTreatment
    ) as Array<ActiveTreatmentItem>;
    const nfbActivesSummary = activesSummaryOf(nfbCardActiveItems, "nfb", {});
    const psyActivesSummary = activesSummaryOf(activeTreatmentItems, "psy", spansByTreatment);
    const nfbActivesLoading = activeTreatmentsResult.isLoading || spansLoading;
    const psyActivesLoading = activeTreatmentsResult.isLoading || spansLoading;

    // Coluna "Ativos" das duas tabelas: o MESMO fetch dos cartões (snapshot, ignora o "Período"),
    // com as regras de atribuição de cada tabela; os pivots misturam terminados + ativos e as
    // medidas separam por estado.
    const activeOwnerItems = scopedActiveOwnerTreatments(
        (activeTreatmentsResult.data || []) as Array<OutcomeTreatmentItem>,
        outcomeFilters,
        includeSuspended,
        EXCLUDED_PERSON_NAMES,
        spansByTreatment
    );
    const caseManagerPivotItems = [...scopedOutcomeItems, ...activeOwnerItems];

    const activePsyCreditItems = scopedActivePsyCredits(
        (activeTreatmentsResult.data || []) as Array<PsyOutcomeTreatmentItem>,
        psyOutcomeFilters,
        includeSuspended,
        EXCLUDED_PERSON_NAMES,
        psychologistUserId,
        spansByTreatment
    );
    const psyPivotItems = [...scopedPsyCredits, ...activePsyCreditItems];

    const outcomesLoading =
        (caseManagerUserId !== null ? caseManagerTechnicianResult.isLoading || finishedTreatmentsResult.isLoading : finishedTreatmentsResult.isLoading) ||
        activeTreatmentsResult.isLoading;
    const outcomesError = finishedTreatmentsResult.isError || activeTreatmentsResult.isError;

    const psyOutcomesLoading =
        (psychologistUserId !== null ? psychologistTechnicianResult.isLoading || psyFinishedTreatmentsResult.isLoading : psyFinishedTreatmentsResult.isLoading) ||
        activeTreatmentsResult.isLoading;
    const psyOutcomesError = psyFinishedTreatmentsResult.isError || activeTreatmentsResult.isError;

    // Drill dos cartões de fase: mesmas regras do resumo (disciplina + fase), 1 tratamento por linha.
    const totalActivesSegmentDrillDownOf = (segment: ActivesDisciplineSegment) => {
        const rows = totalActivesDrillRowsOf(activeTreatmentItems, segment);
        if (rows.length === 0) return null;
        const config: DrillDownConfig = {
            kind: "rows",
            title: `Clientes ativos (total) — ${TOTAL_ACTIVES_SEGMENT_LABELS[segment]}`,
            columns: totalActivesDrillColumns,
            rows,
            defaultSortKey: "-dateOfStart",
            rowRoute: treatmentRoute,
            queryDescription:
                "Um tratamento ativo por linha, com as sessões realizadas de cada disciplina — mesmas regras do cartão, " +
                "por isso a contagem reconcilia com o número mostrado.",
        };
        return config;
    };

    const activesPhaseDrillDownOf = (discipline: ActivesDiscipline, cardTitle: string, treatmentItems: Array<ActiveTreatmentItem>) => (phase: 1 | 2) => {
        const rows = activesPhaseDrillRowsOf(treatmentItems, discipline, spansByTreatment, phase);
        if (rows.length === 0) return null;
        const config: DrillDownConfig = {
            kind: "rows",
            title: `${cardTitle} — Fase ${phase}`,
            columns: activesDrillColumns,
            rows,
            defaultSortKey: "-dateOfStart",
            rowRoute: treatmentRoute,
            queryDescription:
                "Um tratamento ativo por linha, com a perturbação (grupo), o tipo de cliente e as sessões realizadas da disciplina " +
                "— mesmas regras do cartão (disciplina + fase), por isso a contagem reconcilia com o número mostrado.",
        };
        return config;
    };

    const activesScopeText = isDirectorView ? "Clínica inteira." : "Apenas os teus tratamentos.";
    const totalActivesExplanation = {
        metricDefinitions: [
            {
                metric: "Ativos (total)",
                definition: 'tratamentos com estado "Ativo" e pelo menos 1 sessão realizada de NFB ou de Psicologia. Cada tratamento conta UMA vez.',
            },
            { metric: "Ambos", definition: "com sessões realizadas de NFB e de Psicologia." },
            { metric: "Só NFB", definition: "só com sessões realizadas de NFB." },
            { metric: "Só Psicologia", definition: "só com sessões realizadas de Psicologia." },
        ],
        queryDescription:
            'Fotografia atual da clínica inteira, seja qual for a data — o filtro "Período" não se aplica a este cartão. ' +
            "Um cliente nunca tem mais de um tratamento ativo, por isso contar tratamentos é contar clientes.\n\n" +
            'Os cartões de Neurofeedback e Psicologia abaixo não se somam: quem faz as duas disciplinas está em ambos, e aqui está só em "Ambos". ' +
            '"Só Psicologia" + "Ambos" = total do cartão de Psicologia. O cartão de Neurofeedback segue a regra da tabela das gestoras, ' +
            "por isso não reconcilia com este.\n\n" +
            'Grupos de perturbação pelo diagnóstico principal do tratamento (sem diagnóstico conta em "Outros").',
        warningNote:
            'Não compares este número com a coluna "Ativos" das tabelas: as tabelas são por pessoa e exigem, além do estado, que o serviço ' +
            "dessa disciplina esteja a decorrer.",
    };
    const nfbActivesExplanation = {
        metricDefinitions: [
            {
                metric: "Ativos (total)",
                definition:
                    `mesma regra da coluna "Ativos" da tabela "Tratamentos por gestora de caso" (linha Total): tratamentos com estado "Ativo", ` +
                    `com gestora de caso, com pelo menos 1 sessão de NFB realizada e com o Neurofeedback ainda a decorrer ` +
                    `(período de serviço prestado sem data de fim, ou com data de fim ainda por chegar).`,
            },
            { metric: "Fase 1", definition: 'antes do 2.º qEEG realizado (igual à coluna "Ativos F1").' },
            { metric: "Fase 2", definition: 'a partir do 2.º qEEG realizado (igual à coluna "Ativos F2").' },
        ],
        queryDescription:
            `Fotografia atual, seja qual for a data — o filtro "Período" não se aplica a este cartão. ` +
            `Excluídos os tratamentos em que a gestora de caso é também psicóloga do tratamento; segue o filtro "Utilizadores". ` +
            `Os seletores "Perturbação" e "Tipo de cliente" da tabela não se aplicam ao cartão.\n\n` +
            `Grupos de perturbação pelo diagnóstico principal do tratamento (sem diagnóstico conta em "Outros").\n\n${activesScopeText}`,
    };
    const psyActivesExplanation = {
        metricDefinitions: [
            { metric: "Ativos (total)", definition: 'tratamentos com estado "Ativo" e pelo menos 1 sessão de Psicologia realizada.' },
            { metric: "Fase 1", definition: "período de acompanhamento em vigor semanal (ou sem periodicidade definida)." },
            { metric: "Fase 2", definition: "período de acompanhamento em vigor mensal." },
        ],
        queryDescription:
            `Fotografia atual, seja qual for a data — o filtro "Período" não se aplica a estes cartões.\n\n` +
            `Grupos de perturbação pelo diagnóstico principal do tratamento (sem diagnóstico conta em "Outros").\n\n${activesScopeText}`,
    };

    // ---- Drill-downs (validação): células do pivot, barras e linhas mensais abrem os dados subjacentes ----
    // Modo "rows" sobre o fetch raw da secção — as linhas derivam do MESMO predicado do widget
    // (gestoras: inclui a exclusão owner-é-psicóloga, não exprimível em filtro Directus). As
    // pessoas/mês vêm dos itens do próprio nó, por isso o âmbito bate sempre com o número mostrado.
    const treatmentsDrillConfigOf = (
        rows: Array<TreatmentDrillRow>,
        columns: Array<DrillDownColumn>,
        title: string,
        queryDescription: string
    ) => {
        const totalSessions = rows.reduce((sum, row) => sum + row.sessions, 0);
        const config: DrillDownConfig = {
            kind: "rows",
            title,
            columns,
            rows,
            defaultSortKey: "-dateOfStart",
            headerChips: [`${formatQuantity(totalSessions)} sessões`],
            rowRoute: treatmentRoute,
            queryDescription,
        };
        return config;
    };

    const caseManagerDrillDescription =
        "Um tratamento por linha, com o breakdown das sessões de NFB contadas neste valor (mesmo universo do widget, " +
        "incluindo a exclusão de tratamentos geridos pela própria psicóloga e de clientes que nunca compareceram no período). " +
        "O chip de sessões reconcilia com o número do widget.";

    const caseManagerTreatmentsDrill = (personKeys: Set<string> | null, monthIndex: number | null, title: string) => {
        const rows = ownerTreatmentRowsFromSessions(ownerSessionsResult.data || [], personKeys, monthIndex);
        const config = treatmentsDrillConfigOf(rows, caseManagerTreatmentsDrillColumns, title, caseManagerDrillDescription);
        return config;
    };

    const caseManagerCellDrillDown = (node: PivotNode<AttendanceItem>) => {
        if (node.items.length === 0) return null;
        const personKeys = new Set(node.items.map((item) => item.personKey));
        const config = caseManagerTreatmentsDrill(personKeys, node.items[0].monthIndex, `Tratamentos (NFB) — ${node.path.join(" — ")}`);
        return config;
    };

    const caseManagerBarDrillDown = (personKey: string, personName: string) => {
        const config = caseManagerTreatmentsDrill(new Set([personKey]), null, `Tratamentos (NFB) — ${personName}`);
        return config;
    };

    // Psicólogas: mesma UX, atribuição por quem deu a sessão — uma linha por (tratamento, psicóloga);
    // sessão de Psicologia sem tratamento aparece como linha própria sem ficha.
    const psychologistDrillDescription =
        "Um tratamento por linha (por psicóloga — atribuição por quem deu a sessão), com o breakdown das sessões de Psicologia " +
        "contadas neste valor, excluindo clientes que nunca compareceram no período. Sessões sem tratamento associado aparecem " +
        "numa linha própria. O chip de sessões reconcilia com o número do widget.";

    const psychologistTreatmentsDrill = (personKeys: Set<string> | null, monthIndex: number | null, title: string) => {
        const rows = psychologistTreatmentRowsFromSessions(psychologistSessionsResult.data || [], personKeys, monthIndex);
        const config = treatmentsDrillConfigOf(rows, psychologistTreatmentsDrillColumns, title, psychologistDrillDescription);
        return config;
    };

    const psychologistCellDrillDown = (node: PivotNode<AttendanceItem>) => {
        if (node.items.length === 0) return null;
        const personKeys = new Set(node.items.map((item) => item.personKey));
        const config = psychologistTreatmentsDrill(personKeys, node.items[0].monthIndex, `Tratamentos (Psicologia) — ${node.path.join(" — ")}`);
        return config;
    };

    const psychologistBarDrillDown = (personKey: string, personName: string) => {
        const config = psychologistTreatmentsDrill(new Set([personKey]), null, `Tratamentos (Psicologia) — ${personName}`);
        return config;
    };

    // Vistas próprias (tabela mensal): mesmo drill por tratamento, restringido ao mês da linha —
    // o fetch já vem filtrado pelo viewer, por isso personKeys=null é "todas as minhas clientes".
    const caseManagerMonthlyRowDrillDown = (rowIndex: number) => {
        const monthRow = monthlyAttendanceTableRows(caseManagerItems)[rowIndex];
        if (!monthRow) return null;
        const config = caseManagerTreatmentsDrill(null, monthRow.monthIndex, `Tratamentos (NFB) — ${monthRow.monthLabel}`);
        return config;
    };

    const psychologistMonthlyRowDrillDown = (rowIndex: number) => {
        const monthRow = monthlyAttendanceTableRows(psychologistItems)[rowIndex];
        if (!monthRow) return null;
        const config = psychologistTreatmentsDrill(null, monthRow.monthIndex, `Tratamentos (Psicologia) — ${monthRow.monthLabel}`);
        return config;
    };

    const periodDescription = `Período: ${formatDateRangePt(timelineRange)}.`;

    const COMMITMENT_EXCLUSION_WARNING =
        "Excluídos: tratamentos em que o cliente nunca compareceu a nenhuma sessão no período (0 realizadas) — " +
        "sem uma primeira presença, o profissional não tem oportunidade de criar compromisso com o cliente.";

    const attendanceExplanation = (groupDescription: string) => {
        const explanation = {
            metricDefinitions: [
                { metric: "Sessões", definition: "realizadas + faltas + canceladas." },
                { metric: "% comparência", definition: "realizadas ÷ (realizadas + faltas + canceladas)." },
            ],
            queryDescription: `Contam as sessões ${groupDescription}\n\n${periodDescription}`,
            warningNote: COMMITMENT_EXCLUSION_WARNING,
            businessNote: `Objetivo de comparência: ${ATTENDANCE_TARGET_PCT}%.`,
        };
        return explanation;
    };

    const caseManagersGroupDescription =
        "de NeuroFeedback das clientes geridas por cada gestora de caso (atribuição pelo dono do tratamento, não por quem deu a sessão). " +
        "Tratamentos em que a gestora é também psicóloga do tratamento não contam aqui — são tratados na secção das psicólogas.";
    const psychologistsGroupDescription = "de Psicologia atribuídas à psicóloga que deu a sessão.";

    const satisfactionExplanation = (attributionDescription: string) => {
        const explanation = {
            metricDefinitions: [
                { metric: "Média (0–10)", definition: 'média da nota "Recomendaria a NeuroImprove?".' },
                { metric: "Questionários", definition: "nº de questionários preenchidos atribuídos à pessoa." },
                { metric: "Total", definition: "questionários distintos — um questionário atribuído a várias pessoas conta uma vez — e a média desses." },
            ],
            queryDescription:
                `Questionários de satisfação preenchidos no período (data de envio), com a nota ${attributionDescription}\n\n` +
                'Cada questionário conta uma vez por pessoa (duplicados da junção removidos); segue o filtro "Utilizadores" (por defeito só ativos).',
            businessNote: "Apenas ~40% dos clientes respondem — ler com prudência.",
        };
        return explanation;
    };

    const satisfactionTrendExplanation = (attributionDescription: string) => {
        const explanation = {
            metricDefinitions: [
                {
                    metric: isDirectorView ? "Linha de cada pessoa" : "Linha",
                    definition: 'média da nota "Recomendaria a NeuroImprove?" (0–10) por trimestre.',
                },
                ...(isDirectorView
                    ? [{ metric: "Média da equipa", definition: "média por questionário distinto no trimestre — um questionário atribuído a várias pessoas conta uma vez." }]
                    : []),
            ],
            queryDescription:
                `Questionários do período do filtro "Período" (data de envio). Trimestres apenas parcialmente cobertos pelo período contam só os dias incluídos. ` +
                `Nota ${attributionDescription}\n\n` +
                (isDirectorView
                    ? `O filtro "Utilizadores" define o âmbito (por defeito só ativos; "Ativos e suspensos" inclui quem já saiu).\n\n`
                    : "Mostra apenas os teus dados.\n\n") +
                "Passa o rato num ponto para ver o nº de questionários.",
            businessNote: "Apenas ~40% dos clientes respondem — trimestres com poucas respostas oscilam muito.",
        };
        return explanation;
    };

    const outcomesExplanation = {
        metricDefinitions: [
            {
                metric: "Ativos",
                definition:
                    `fotografia atual dos tratamentos da gestora com estado "Ativo" E com o Neurofeedback ainda a decorrer ` +
                    `(período de serviço prestado sem data de fim, ou com data de fim ainda por chegar). NÃO segue o filtro "Período". ` +
                    `Quem já terminou o Neurofeedback deixa de contar, mesmo que o tratamento continue ativo por causa da Psicologia.`,
            },
            {
                metric: "Ativos F1 / F2",
                definition: "repartem os Ativos pela fase do Neurofeedback (Fase 2 = a partir do 2.º qEEG realizado). Somam sempre a Ativos.",
            },
            {
                metric: "Terminados",
                definition: `tratamentos com data de fim no período do filtro "Período", com sucesso ("Alta clínica", "Alta a pedido" ou "Esporádico") ou desistência.`,
            },
            { metric: "% sucesso", definition: "sucessos ÷ (sucessos + desistências)." },
            { metric: "Adultos", definition: "terminados com idade de início > 18." },
            { metric: "Crianças", definition: "terminados com idade de início ≤ 18 ou sem idade registada." },
            { metric: "% suc. adultos / crianças", definition: "% sucesso dentro de cada grupo." },
            { metric: "Média sessões", definition: "média de sessões NFB realizadas por tratamento terminado." },
        ],
        queryDescription:
            `Contam apenas tratamentos com gestora de caso e com pelo menos 1 sessão de NFB realizada. ` +
            `O grupo de perturbação vem do diagnóstico principal do tratamento (sem diagnóstico conta em "Outros").\n\n` +
            `Gestoras ordenadas por nº de ativos (carga atual), descendente. ` +
            `Expande cada gestora para veres o detalhe por grupo de perturbação; clica numa célula para veres os tratamentos por trás do número.\n\n` +
            (isDirectorView ? `O filtro "Utilizadores" define o âmbito (por defeito só ativas).` : "Mostra apenas os teus tratamentos."),
        warningNote:
            "Excluídos: tratamentos em que a gestora de caso é também psicóloga do tratamento — esses contam na secção das psicólogas.",
    };

    const psyOutcomesExplanation = {
        metricDefinitions: [
            {
                metric: "Ativos",
                definition:
                    `fotografia atual dos tratamentos da psicóloga com estado "Ativo" E com a Psicologia ainda a decorrer ` +
                    `(período de serviço prestado sem data de fim, ou com data de fim ainda por chegar). NÃO segue o filtro "Período". ` +
                    `Quem já terminou a Psicologia deixa de contar, mesmo que o tratamento continue ativo por causa do Neurofeedback.`,
            },
            {
                metric: "Terminados",
                definition: `tratamentos com data de fim no período do filtro "Período", com sucesso ("Alta clínica", "Alta a pedido" ou "Esporádico") ou desistência.`,
            },
            { metric: "% sucesso", definition: "sucessos ÷ (sucessos + desistências)." },
            { metric: "Adultos", definition: "terminados com idade de início > 18." },
            { metric: "Crianças", definition: "terminados com idade de início ≤ 18 ou sem idade registada." },
            { metric: "% suc. adultos / crianças", definition: "% sucesso dentro de cada grupo." },
            { metric: "Média sessões", definition: "média de sessões de Psicologia realizadas por tratamento terminado." },
        ],
        queryDescription:
            `Contam apenas tratamentos com pelo menos 1 sessão de Psicologia realizada; a atribuição é às psicólogas do tratamento. ` +
            `O grupo de perturbação vem do diagnóstico principal (sem diagnóstico conta em "Outros").\n\n` +
            `Psicólogas ordenadas por nº de ativos (carga atual), descendente. ` +
            `Expande cada psicóloga para veres o detalhe por grupo de perturbação; clica numa célula para veres os tratamentos por trás do número.\n\n` +
            (isDirectorView ? `O filtro "Utilizadores" define o âmbito (por defeito só ativas).` : "Mostra apenas os teus tratamentos."),
        warningNote:
            "Um tratamento com várias psicólogas conta para cada uma — a linha Total conta tratamentos distintos, por isso pode ser menor " +
            "que a soma das linhas. Inclui os tratamentos em que a gestora de caso é também psicóloga (excluídos da tabela das gestoras).",
    };

    const satisfactionTrendTooltip: TimeSeriesTooltipConfig = {
        valueFormatter: (value) => formatDecimal(value, 2),
        countSuffixKey: TREND_COUNT_SUFFIX,
        countLabel: "questionários",
    };

    const monthlyColumns = [
        { key: "month", label: "Mês" },
        { key: "sessions", label: "Sessões", isNumeric: true },
        { key: "rate", label: "% comparência", isNumeric: true },
    ];
    const monthlyDisplayRows = (items: Array<AttendanceItem>) => {
        const rows = monthlyAttendanceTableRows(items).map((row) => ({
            month: row.monthLabel,
            sessions: formatQuantity(row.sessions),
            rate: row.ratePct === null ? "—" : formatPercent(row.ratePct, 1),
        }));
        return rows;
    };

    const accumulatedRateCard = (items: Array<AttendanceItem>, groupDescription: string, isLoading: boolean, drillDown: DrillDownConfig) => {
        const ratePct = attendanceRateOfItems(items);
        const card = (
            <StatCard
                label={`Taxa de comparência acumulada (objetivo ${ATTENDANCE_TARGET_PCT}%)`}
                value={ratePct === null ? "—" : formatPercent(ratePct, 2)}
                color={ratePct !== null && ratePct >= ATTENDANCE_TARGET_PCT ? DASHBOARD_COLORS.done : DASHBOARD_COLORS.cancelled}
                explanation={attendanceExplanation(groupDescription)}
                isLoading={isLoading}
                drillDown={drillDown}
            />
        );
        return card;
    };

    return (
        <DashboardPage globalFilters={teamKpisGlobalFilters} values={values} onChange={setValue}>
            {isDirectorView &&
                totalActivesCard(
                    totalActivesSummary,
                    totalActivesExplanation,
                    activeTreatmentsResult.isLoading,
                    activeTreatmentsResult.isError,
                    totalActivesSegmentDrillDownOf
                )}

            {showCaseManagersSection &&
                activesCard(
                    "Clientes ativos — Neurofeedback",
                    nfbActivesSummary,
                    nfbActivesExplanation,
                    nfbActivesLoading,
                    activeTreatmentsResult.isError || activeSpansResult.isError,
                    activesPhaseDrillDownOf("nfb", "Clientes ativos (NFB)", nfbCardActiveItems)
                )}

            {showCaseManagersSection && (
                <PivotTableCard
                    title="Tratamentos por gestora de caso"
                    explanation={outcomesExplanation}
                    pivot={outcomePivotOf(caseManagerPivotItems, outcomeClientType, spansByTreatment)}
                    rowHeaderLabel="Gestora / Perturbação"
                    maxBodyHeight={420}
                    headerControls={
                        <>
                            <HeaderSelect
                                label="Perturbação"
                                allLabel="Todas"
                                options={COARSE_DISORDER_GROUPS.map((group) => ({ value: group.key, label: group.label }))}
                                value={outcomeGroupKey}
                                onChange={(newValue) => setOutcomeGroupKey(newValue as CoarseDisorderGroupKey | null)}
                            />
                            <HeaderSelect
                                label="Tipo de cliente"
                                allLabel="Todos"
                                options={OUTCOME_CLIENT_TYPE_OPTIONS}
                                value={outcomeClientType}
                                onChange={(newValue) => setOutcomeClientType(newValue as ClientTypeKey | null)}
                            />
                        </>
                    }
                    isLoading={outcomesLoading}
                    isError={outcomesError}
                    cellDrillDown={outcomeCellDrillDown}
                />
            )}

            {showPsychologistsSection &&
                activesCard(
                    "Clientes ativos — Psicologia",
                    psyActivesSummary,
                    psyActivesExplanation,
                    psyActivesLoading,
                    activeTreatmentsResult.isError || activeSpansResult.isError,
                    activesPhaseDrillDownOf("psy", "Clientes ativos (Psicologia)", activeTreatmentItems)
                )}

            {showPsychologistsSection && (
                <PivotTableCard
                    title="Tratamentos por psicóloga"
                    explanation={psyOutcomesExplanation}
                    pivot={psyOutcomePivotOf(psyPivotItems, psyOutcomeClientType, spansByTreatment)}
                    rowHeaderLabel="Psicóloga / Perturbação"
                    maxBodyHeight={420}
                    headerControls={
                        <>
                            <HeaderSelect
                                label="Perturbação"
                                allLabel="Todas"
                                options={COARSE_DISORDER_GROUPS.map((group) => ({ value: group.key, label: group.label }))}
                                value={psyOutcomeGroupKey}
                                onChange={(newValue) => setPsyOutcomeGroupKey(newValue as CoarseDisorderGroupKey | null)}
                            />
                            <HeaderSelect
                                label="Tipo de cliente"
                                allLabel="Todos"
                                options={OUTCOME_CLIENT_TYPE_OPTIONS}
                                value={psyOutcomeClientType}
                                onChange={(newValue) => setPsyOutcomeClientType(newValue as ClientTypeKey | null)}
                            />
                        </>
                    }
                    isLoading={psyOutcomesLoading}
                    isError={psyOutcomesError}
                    cellDrillDown={psyOutcomeCellDrillDown}
                />
            )}

            {showCaseManagersSection && (
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={6}>
                        {isDirectorView ? (
                            <PivotTableCard
                                title="Comparência - Gestoras de caso"
                                explanation={attendanceExplanation(caseManagersGroupDescription)}
                                pivot={attendancePivotOf(caseManagerItems)}
                                rowHeaderLabel="Mês / Gestora"
                                maxBodyHeight={420}
                                isLoading={caseManagersLoading}
                                isError={caseManagersError}
                                cellDrillDown={caseManagerCellDrillDown}
                            />
                        ) : (
                            <DataTableCard
                                title="Comparência das tuas clientes (NFB) por mês"
                                explanation={attendanceExplanation(caseManagersGroupDescription)}
                                columns={monthlyColumns}
                                rows={monthlyDisplayRows(caseManagerItems)}
                                isLoading={caseManagersLoading}
                                isError={caseManagersError}
                                rowDrillDown={caseManagerMonthlyRowDrillDown}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        {isDirectorView
                            ? attendanceBarsCard(
                                  "Comparência acumulada - Gestoras de caso",
                                  attendanceExplanation(caseManagersGroupDescription),
                                  caseManagerItems,
                                  caseManagersLoading,
                                  caseManagersError,
                                  caseManagerBarDrillDown
                              )
                            : accumulatedRateCard(
                                  caseManagerItems,
                                  caseManagersGroupDescription,
                                  caseManagersLoading,
                                  caseManagerTreatmentsDrill(null, null, "Tratamentos (NFB) — acumulado do período")
                              )}
                    </Grid>
                </Grid>
            )}

            {showPsychologistsSection && (
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={6}>
                        {isDirectorView ? (
                            <PivotTableCard
                                title="Comparência - Psicólogas"
                                explanation={attendanceExplanation(psychologistsGroupDescription)}
                                pivot={attendancePivotOf(psychologistItems)}
                                rowHeaderLabel="Mês / Psicóloga"
                                maxBodyHeight={420}
                                isLoading={psychologistsLoading}
                                isError={psychologistsError}
                                cellDrillDown={psychologistCellDrillDown}
                            />
                        ) : (
                            <DataTableCard
                                title="As tuas sessões de Psicologia por mês"
                                explanation={attendanceExplanation(psychologistsGroupDescription)}
                                columns={monthlyColumns}
                                rows={monthlyDisplayRows(psychologistItems)}
                                isLoading={psychologistsLoading}
                                isError={psychologistsError}
                                rowDrillDown={psychologistMonthlyRowDrillDown}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        {isDirectorView
                            ? attendanceBarsCard(
                                  "Comparência acumulada - Psicólogas",
                                  attendanceExplanation(psychologistsGroupDescription),
                                  psychologistItems,
                                  psychologistsLoading,
                                  psychologistsError,
                                  psychologistBarDrillDown
                              )
                            : accumulatedRateCard(
                                  psychologistItems,
                                  psychologistsGroupDescription,
                                  psychologistsLoading,
                                  psychologistTreatmentsDrill(null, null, "Tratamentos (Psicologia) — acumulado do período")
                              )}
                    </Grid>
                </Grid>
            )}

            <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "error.light", backgroundColor: "#FDF3F3" }}>
                <Typography variant="subtitle2" color="error" fontWeight={700} textAlign="center">
                    !! Atenção Questionários de satisfação !!
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    {TEAM_KPIS_SURVEY_WARNING}
                </Typography>
            </Paper>

            <Grid container spacing={3}>
                {showCaseManagersSection && (
                    <Grid item xs={12} lg={6}>
                        <DataTableCard
                            title="Satisfação dos clientes - por gestora de caso"
                            explanation={satisfactionExplanation("atribuída à gestora de caso dona do tratamento; questionários de tratamentos em que a gestora é também psicóloga do tratamento contam apenas na atribuição das psicólogas.")}
                            columns={SATISFACTION_COLUMNS}
                            rows={satisfactionDisplayRows(ownerSatisfaction.rows)}
                            rowDrillDown={satisfactionRowDrillDown(ownerSatisfaction.rows, "owner")}
                            totalsRow={{
                                person: "Total (questionários distintos)",
                                avg: ownerSatisfaction.totals.avg === null ? "—" : formatDecimal(ownerSatisfaction.totals.avg, 2),
                                surveys: formatQuantity(ownerSatisfaction.totals.surveys),
                            }}
                            isLoading={surveysResult.isLoading}
                            isError={surveysResult.isError}
                        />
                    </Grid>
                )}
                {showPsychologistsSection && (
                    <Grid item xs={12} lg={6}>
                        <DataTableCard
                            title="Satisfação dos clientes - por psicóloga"
                            explanation={satisfactionExplanation("atribuída às psicólogas envolvidas no tratamento.")}
                            columns={SATISFACTION_COLUMNS}
                            rows={satisfactionDisplayRows(psychologistSatisfaction.rows)}
                            rowDrillDown={satisfactionRowDrillDown(psychologistSatisfaction.rows, "psychologists")}
                            totalsRow={{
                                person: "Total (questionários distintos)",
                                avg: psychologistSatisfaction.totals.avg === null ? "—" : formatDecimal(psychologistSatisfaction.totals.avg, 2),
                                surveys: formatQuantity(psychologistSatisfaction.totals.surveys),
                            }}
                            isLoading={surveysResult.isLoading}
                            isError={surveysResult.isError}
                        />
                    </Grid>
                )}
            </Grid>

            <Grid container spacing={3}>
                {showCaseManagersSection && (
                    <Grid item xs={12} lg={6}>
                        <WidgetCard
                            title="Satisfação por trimestre - gestoras de caso"
                            explanation={satisfactionTrendExplanation("atribuída à gestora de caso dona do tratamento; questionários de tratamentos em que a gestora é também psicóloga do tratamento contam apenas na atribuição das psicólogas.")}
                            bodyHeight={340}
                            isLoading={surveysResult.isLoading}
                            isError={surveysResult.isError}
                        >
                            <TimeSeriesChart
                                points={ownerTrend.points}
                                series={applyTechnicianSeriesColors(ownerTrend.series, technicianColorByUserId)}
                                height={320}
                                showPointLabels={false}
                                tooltip={satisfactionTrendTooltip}
                                yDomain={[6, 10.5]}
                                yTicks={[6, 7, 8, 9, 10]}
                            />
                        </WidgetCard>
                    </Grid>
                )}
                {showPsychologistsSection && (
                    <Grid item xs={12} lg={6}>
                        <WidgetCard
                            title="Satisfação por trimestre - psicólogas"
                            explanation={satisfactionTrendExplanation("atribuída às psicólogas envolvidas no tratamento.")}
                            bodyHeight={340}
                            isLoading={surveysResult.isLoading}
                            isError={surveysResult.isError}
                        >
                            <TimeSeriesChart
                                points={psychologistTrend.points}
                                series={applyTechnicianSeriesColors(psychologistTrend.series, technicianColorByUserId)}
                                height={320}
                                showPointLabels={false}
                                tooltip={satisfactionTrendTooltip}
                                yDomain={[6, 10.5]}
                                yTicks={[6, 7, 8, 9, 10]}
                            />
                        </WidgetCard>
                    </Grid>
                )}
            </Grid>
        </DashboardPage>
    );
};
